"use client";

/**
 * Landing Page - Features Section Component
 */

import { useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { Button } from "@/shared/presentation/components/ui/button";
import { Modal } from "@/shared/presentation/components/custom";
import { FEATURE_ITEMS } from "../../constants";

export function FeaturesSection() {
  const t = useTranslations();
  const [isLearnMoreModalOpen, setIsLearnMoreModalOpen] = useState(false);

  return (
    <section>
      <div id="about_us" className="h-[1px] bg-transparent" />
      <div className="px-4 sm:px-8 lg:px-4">
        {FEATURE_ITEMS.map((feature, index) => (
          <div key={feature.id} className="mx-auto max-w-5xl">
            {index > 0 && <hr className="mx-4 mt-5 sm:mx-8" />}

            {/* Mobile Layout - Image on top, content below */}
            <div className="my-8 flex flex-col gap-6 text-left md:hidden">
              <div className="w-full overflow-hidden rounded-lg">
                <Image
                  src={feature.imageSrc}
                  alt={t(feature.imageAlt)}
                  width={500}
                  height={300}
                  className="h-auto w-full rounded-lg object-cover"
                />
              </div>
              <div>
                <h3 className="text-xl font-bold leading-8 text-primary-dark sm:text-2xl">
                  {t(feature.title)}
                </h3>
                <p className="mx-0 my-3 text-sm text-foreground">
                  {t(feature.description)}
                </p>
                <Button
                  onClick={() => setIsLearnMoreModalOpen(true)}
                  className="my-2 rounded-md bg-primary px-4 py-2 text-center capitalize text-white hover:bg-opacity-90"
                >
                  {t("landing.features.learnMore")}
                </Button>
              </div>
            </div>

            {/* Desktop Layout - Side by side */}
            <div className="my-10 hidden gap-4 text-left md:flex">
              {/* Image on Left */}
              {feature.imagePosition === "left" && (
                <div className="my-auto flex-1">
                  <Image
                    src={feature.imageSrc}
                    alt={t(feature.imageAlt)}
                    width={500}
                    height={300}
                    className="rounded-lg"
                  />
                </div>
              )}

              {/* Content */}
              <div className="my-auto flex-1">
                <h3 className="text-2xl font-bold leading-10 text-primary-dark">
                  {t(feature.title)}
                </h3>
                <p className="mx-0 my-4 text-sm text-foreground">
                  {t(feature.description)}
                </p>
                <Button
                  onClick={() => setIsLearnMoreModalOpen(true)}
                  className="my-4 rounded-md bg-primary px-4 py-2 text-center capitalize text-white hover:bg-opacity-90"
                >
                  {t("landing.features.learnMore")}
                </Button>
              </div>

              {/* Image on Right */}
              {feature.imagePosition === "right" && (
                <div className="my-auto flex-1">
                  <Image
                    src={feature.imageSrc}
                    alt={t(feature.imageAlt)}
                    width={500}
                    height={300}
                    className="rounded-lg"
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Learn More Modal */}
      <Modal
        isOpen={isLearnMoreModalOpen}
        onClose={() => setIsLearnMoreModalOpen(false)}
        title={t("landing.features.learnMoreModal.title")}
        contentClassName="max-w-2xl"
        footer={
          <Button
            variant="secondary"
            onClick={() => setIsLearnMoreModalOpen(false)}
          >
            {t("common.close")}
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="text-left text-primary-dark">
            {t("landing.features.learnMoreModal.paragraph1")}
          </p>
          <p className="text-left text-primary-dark">
            {t("landing.features.learnMoreModal.paragraph2")}
          </p>
          <p className="text-left text-primary-dark">
            {t("landing.features.learnMoreModal.paragraph3")}
          </p>
        </div>
      </Modal>
    </section>
  );
}

export default FeaturesSection;
